import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Globe, Link2, Search, Users, Plug } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { authFetch } from "../utils/authFetch";
import { describePartnerReach, describePersonReach, type PersonReach } from "./audience";

interface WhoCanOpenData {
  audience: "private" | "shared" | "partner" | "public";
  published: boolean;
  /** Every partner that can open it; on a Public project `partners` lists only
   *  the ones with settings of their own. */
  everyPartner: number;
  partnerTotal: number;
  /** A Public project's exceptions: partners that can't open it. */
  partnersShutOut: Array<{ id: string; slug: string; name: string; reason: PersonReach["reason"] }>;
  /** Addresses asked (deactivated ones are not). */
  outsideTotal: number;
  joveo: { anyoneRole: "none" | "viewer" | "editor" | "admin"; people: number;
           members: { admin: number; editor: number; viewer: number }; superAdmins: number };
  partners: Array<{ id: string; slug: string; name: string; via: "everyone" | "rules" | "default" | "enable";
                    mode: PersonReach["mode"]; ruleCount: number }>;
  outside: Array<{ id: string; email: string; name: string | null; pending: boolean;
                   via: PersonReach["via"]; partners: string[]; mode: PersonReach["mode"]; ruleCount: number }>;
  shareLinks: number;
  integrations: number;
}

const ROLE: Record<string, string> = { none: "No access", viewer: "Viewer", editor: "Editor", admin: "Admin" };
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** A project's Members tab: everyone who can open it, and why — the server's
 *  resolver answer (GET /api/admin/projects/{id}/who-can-open). Read-only;
 *  each Change goes to the editor that owns that grant. */
export default function WhoCanOpen({ projectId, refreshKey = "" }: {
  projectId: string;
  /** Changes when the editor above changes something, so this answer follows. */
  refreshKey?: string;
}) {
  // The partner and External Users pages are super-admin-only in Settings; a
  // project admin gets the answer without links that would lead nowhere.
  const canManage = useAuth().user?.isSuperAdmin ?? false;
  const [data, setData] = useState<WhoCanOpenData | null>(null);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState("");
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const ctrl = new AbortController();
    setFailed(false);
    (async () => {
      try {
        const r = await authFetch(`/api/admin/projects/${projectId}/who-can-open`, { signal: ctrl.signal });
        if (ctrl.signal.aborted) return;
        if (r.ok) setData(await r.json()); else setFailed(true);
      } catch (e) {
        if ((e as Error).name !== "AbortError") setFailed(true);
      }
    })();
    return () => ctrl.abort();
  }, [projectId, refreshKey]);
  // A different project starts from scratch; a refresh keeps what is shown.
  useEffect(() => { setData(null); setQuery(""); setShowAll(false); }, [projectId]);

  const outside = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return q ? data.outside.filter(o => o.email.toLowerCase().includes(q) || (o.name || "").toLowerCase().includes(q))
             : data.outside;
  }, [data, query]);

  if (failed) return <p className="text-xs text-red-600">Couldn't load who can open this project.</p>;
  if (!data) return <p className="text-xs text-slate-400">Loading who can open this project…</p>;

  const { joveo } = data;
  const named = [
    joveo.members.admin && plural(joveo.members.admin, "admin"),
    joveo.members.editor && plural(joveo.members.editor, "editor"),
    joveo.members.viewer && plural(joveo.members.viewer, "viewer"),
  ].filter(Boolean).join(", ");
  // On a Public project everyone outside reads it; the names are a lookup,
  // not the point, so they wait behind the search box.
  const collapsed = data.audience === "public" && !showAll && !query.trim();

  return (
    <section aria-label="Who can open this" className="space-y-4">
      <h2 className="text-sm font-semibold text-slate-700">Who can open this</h2>

      <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
        <div className="flex items-start gap-3 px-4 py-3">
          <Users className="size-4 text-indigo-500 mt-0.5 shrink-0" />
          <div className="min-w-0 text-xs text-slate-600 space-y-0.5">
            <div className="text-[13px] font-medium text-slate-800">Joveo</div>
            <div>Anyone at Joveo: {ROLE[joveo.anyoneRole]}{joveo.anyoneRole !== "none" ? ` · ${plural(joveo.people, "person", "people")}` : ""}</div>
            <div>Named members: {named || "none above Anyone at Joveo"}</div>
            <div>Super admins: {joveo.superAdmins}</div>
          </div>
        </div>

        {data.audience === "private" || !data.published ? (
          <div className="flex items-start gap-3 px-4 py-3">
            <Globe className="size-4 text-slate-400 mt-0.5 shrink-0" />
            <p className="text-xs text-slate-600">
              {data.audience === "private"
                ? "Nobody outside Joveo can open this project."
                : "Not published yet, so nobody outside Joveo can open it."}
            </p>
          </div>
        ) : (
          <>
            <div className="px-4 py-3">
              <div className="flex items-center gap-3 mb-1.5">
                <Building2 className="size-4 text-indigo-500 shrink-0" />
                <h3 className="text-[13px] font-medium text-slate-800">Partners ({data.everyPartner})</h3>
              </div>
              {data.audience === "public" && data.everyPartner > 0 && (
                <p className="text-xs text-slate-600 pl-7 mb-1">
                  {data.partnersShutOut.length === 0
                    ? "Every partner, as a Public project"
                    : `${data.everyPartner} of ${data.partnerTotal} partners, as a Public project. Shut out: ${data.partnersShutOut.map(p => p.name).join(", ")}`}
                  {data.partners.length ? ". With settings of their own:" : "."}
                </p>
              )}
              {data.everyPartner === 0
                ? <p className="text-xs text-slate-400 pl-7">No partner can open it.</p>
                : data.partners.length > 0 && <ul className="pl-7 space-y-1">
                    {data.partners.map(p => (
                      <li key={p.id} className="flex items-center gap-2 text-xs">
                        <span className="font-medium text-slate-700 truncate">{p.name}</span>
                        <span className="text-slate-500 truncate flex-1">
                          {describePartnerReach({ mode: p.mode, ruleCount: p.ruleCount, canRead: true, via: p.via, reason: null })}
                        </span>
                        {canManage && (
                          <Link to={`/studio/settings/partners/${encodeURIComponent(p.slug)}?tab=access`}
                            aria-label={`Change ${p.name}'s access`}
                            className="text-[11px] text-indigo-600 hover:underline shrink-0">Change</Link>
                        )}
                      </li>
                    ))}
                  </ul>}
            </div>

            <div className="px-4 py-3">
              <div className="flex items-center gap-3 mb-1.5">
                <Globe className="size-4 text-indigo-500 shrink-0" />
                <h3 className="text-[13px] font-medium text-slate-800">People outside Joveo ({data.outside.length})</h3>
              </div>
              <div className="pl-7 space-y-2">
                {data.audience === "public" && (
                  <p className="text-xs text-slate-600">
                    {data.outside.length === data.outsideTotal
                      ? <>Everyone outside Joveo · {plural(data.outside.length, "person", "people")}</>
                      : <>{data.outside.length} of {plural(data.outsideTotal, "person", "people")} outside Joveo</>}
                    {collapsed && data.outside.length > 0 && (
                      <button type="button" onClick={() => setShowAll(true)}
                        className="ml-2 text-[11px] text-indigo-600 hover:underline">Show all</button>
                    )}
                  </p>
                )}
                {data.outside.length > 5 && (
                  <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 max-w-xs">
                    <Search className="size-3 text-slate-400 shrink-0" />
                    <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Find a person…"
                      aria-label="Find a person"
                      className="flex-1 min-w-0 text-xs text-slate-700 placeholder:text-slate-400 bg-transparent outline-none" />
                  </div>
                )}
                {data.outside.length === 0
                  ? <p className="text-xs text-slate-400">Nobody outside Joveo can open it.</p>
                  : !collapsed && (outside.length === 0
                      ? <p className="text-xs text-slate-400">Nobody matches.</p>
                      : <ul className="space-y-1">
                          {outside.map(o => (
                            <li key={o.id} className="flex items-center gap-2 text-xs">
                              <span className="font-medium text-slate-700 truncate">{o.name || o.email}</span>
                              {o.pending && <span className="text-[10px] text-amber-700 bg-amber-50 px-1.5 rounded shrink-0">pending invite</span>}
                              <span className="text-slate-500 truncate flex-1">
                                {describePersonReach({ mode: o.mode, ruleCount: o.ruleCount, canRead: true,
                                  via: o.via, partners: o.partners, reason: null })}
                              </span>
                              {canManage && (
                                <Link to={`/studio/settings/external-users?open=${encodeURIComponent(o.id)}`}
                                  aria-label={`Change ${o.name || o.email}'s access`}
                                  className="text-[11px] text-indigo-600 hover:underline shrink-0">Change</Link>
                              )}
                            </li>
                          ))}
                        </ul>)}
              </div>
            </div>
          </>
        )}

        <div className="flex items-start gap-3 px-4 py-3">
          <Link2 className="size-4 text-slate-400 mt-0.5 shrink-0" />
          <div className="text-xs text-slate-600 space-y-0.5">
            <h3 className="text-[13px] font-medium text-slate-800">Other ways in</h3>
            <div>
              {data.shareLinks > 0
                ? <Link to="?tab=share-links" className="text-indigo-600 hover:underline">{plural(data.shareLinks, "live share link")}</Link>
                : "No live share links"}
            </div>
            <div className="flex items-center gap-1.5">
              <Plug className="size-3 text-slate-400" />
              {data.integrations > 0 ? plural(data.integrations, "API integration") : "No API integrations"}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
